// Which corpus the corpus-backed panels are reading.
//
// THIS EXISTS BECAUSE THE ANSWER WAS SILENTLY WRONG. Every panel here used to
// pass `orgSlug` through as `corpus_id`, on a comment that said "one corpus per
// org". An org can own several — `acme` owns three, and none of them is named
// `acme` — so the request resolved to a corpus that does not exist. It was
// still authorized, because `_owns_corpus` keeps a `corpus_id == org_slug`
// fallback for corpora that exist in the analytics database with nothing in
// storage, and it came back `reconciled: false`. Impact radius, prerequisite
// debt and the divergence map therefore reported "Pass 2 has never run" about
// fully reconciled data, indefinitely, while `just seed-verify` stayed green
// because `scripts/seed/checks.py` iterates the real ids.
//
// Renders NOTHING for an org that owns one corpus. A picker with one option is
// a question with one answer, and the single-corpus case is the common one.

import { ChipGroup } from "../../ui";
import type { CorpusSummary } from "./api";
import { CORPUS_PICKER_LABEL, CORPUS_UNRECONCILED_SUFFIX } from "./metricLabels";
import "./analytics.css";

export type CorpusPickerProps = {
  corpora: CorpusSummary[];
  value: string | null;
  onChange: (corpusId: string) => void;
};

/** The corpus a screen should read, given the URL's choice and what exists.
 *
 * Exported for the test: this is the rule, and asserting it through a rendered
 * chip row would pin the drawing instead. A `?corpus=` naming something the org
 * does not own falls back to the first owned corpus rather than passing the
 * unknown id to the API — a pasted link from another tenant must not decide
 * what we ask for.
 */
export function resolveCorpus(
  corpora: CorpusSummary[],
  requested: string | undefined,
): string | null {
  if (corpora.length === 0) return null;
  if (requested && corpora.some((c) => c.corpus_id === requested)) return requested;
  return corpora[0].corpus_id;
}

export function CorpusPicker({ corpora, value, onChange }: CorpusPickerProps) {
  if (corpora.length < 2) return null;
  return (
    <div className="maisight-analytics-corpus">
      <ChipGroup
        label={CORPUS_PICKER_LABEL}
        value={value ?? corpora[0].corpus_id}
        onChange={(next) => onChange(String(next))}
        options={corpora.map((corpus) => ({
          value: corpus.corpus_id,
          // An unreconciled corpus is still selectable — hiding it would make
          // "Pass 2 has not run for this one" unanswerable from the screen.
          label: corpus.reconciled
            ? corpus.corpus_id
            : `${corpus.corpus_id} ${CORPUS_UNRECONCILED_SUFFIX}`,
        }))}
      />
    </div>
  );
}
